// Bookmark — BlockNote 커스텀 블록. 웹 링크를 노션식 카드로 표시.
//
// - props.url / title / description. 외부 메타데이터 fetch 는 CORS 로 불가하므로 시도하지 않음
//   (제목 기본값은 도메인, 사용자가 인라인 편집).
// - 파비콘: Google favicon 서비스 (https://www.google.com/s2/favicons?domain=<host>&sz=32).
// - 카드 클릭 → 새 탭. 제목 input 클릭은 stopPropagation 으로 편집만.
// - 읽기 전용(editor.isEditable === false): 카드 표시 + 새 탭 열기만.
//
// ⚠️ createReactBlockSpec 은 생성 함수 반환 — 끝의 () 호출 필수(2026-06-18 사고, LinkedDatabaseView.jsx).

import { useEffect, useState } from 'react';
import { createReactBlockSpec } from '@blocknote/react';
import './custom-blocks.css';

function normalizeUrl(raw) {
  const v = String(raw || '').trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
}

function hostOf(url) {
  try { return new URL(url).hostname; } catch { return ''; }
}

function BookmarkView({ block, editor }) {
  const editable = editor.isEditable;
  const { url, title, description } = block.props;
  const [urlDraft, setUrlDraft] = useState(url || '');
  const [editingUrl, setEditingUrl] = useState(false);
  const [titleDraft, setTitleDraft] = useState(title || '');

  // 다른 인스턴스/협업 갱신으로 props 가 바뀌면 로컬 초안 동기화.
  useEffect(() => { setTitleDraft(title || ''); }, [title]);
  useEffect(() => { setUrlDraft(url || ''); }, [url]);

  const submitUrl = () => {
    const next = normalizeUrl(urlDraft);
    if (!next || !hostOf(next)) return;
    editor.updateBlock(block, {
      props: {
        url: next,
        // 제목이 비어 있으면 도메인을 기본 제목으로.
        title: (block.props.title || '').trim() || hostOf(next),
      },
    });
    setEditingUrl(false);
  };

  const commitTitle = () => {
    const next = titleDraft.trim() || hostOf(url) || url;
    if (next !== (block.props.title || '')) {
      editor.updateBlock(block, { props: { title: next } });
    }
  };

  // URL 미설정(또는 변경 중) + 편집 가능 → 입력 UI.
  if (editable && (!url || editingUrl)) {
    return (
      <div className="bn-bookmark" contentEditable={false}>
        <div className="bn-url-form">
          <span className="bn-url-form-icon">🔖</span>
          <input
            className="bn-url-input"
            type="text"
            value={urlDraft}
            placeholder="북마크할 URL 을 입력하세요 (예: https://example.com)"
            spellCheck={false}
            onChange={(e) => setUrlDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); submitUrl(); }
              else if (e.key === 'Escape' && url) { e.preventDefault(); setUrlDraft(url); setEditingUrl(false); }
            }}
          />
          <button
            type="button"
            className="bn-url-submit"
            disabled={!normalizeUrl(urlDraft) || !hostOf(normalizeUrl(urlDraft))}
            onClick={submitUrl}
          >
            북마크 만들기
          </button>
        </div>
      </div>
    );
  }

  if (!url) {
    // 읽기 전용인데 URL 미설정 — 빈 안내.
    return (
      <div className="bn-bookmark" contentEditable={false}>
        <div className="bn-bookmark-card"><span className="bn-bookmark-domain">비어 있는 북마크</span></div>
      </div>
    );
  }

  const host = hostOf(url);
  const open = () => window.open(url, '_blank', 'noopener,noreferrer');

  return (
    <div className="bn-bookmark" contentEditable={false}>
      <div
        className="bn-bookmark-card"
        onClick={open}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') open(); }}
        title={url}
      >
        {host ? (
          <img
            className="bn-bookmark-favicon"
            src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=32`}
            alt=""
            loading="lazy"
            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
          />
        ) : null}
        <div className="bn-bookmark-body">
          {editable ? (
            <input
              className="bn-bookmark-title"
              value={titleDraft}
              spellCheck={false}
              aria-label="북마크 제목"
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') { e.preventDefault(); commitTitle(); e.currentTarget.blur(); }
              }}
            />
          ) : (
            <span className="bn-bookmark-title">{title || host || url}</span>
          )}
          {description ? <span className="bn-bookmark-desc">{description}</span> : null}
          <span className="bn-bookmark-domain">{host || url}</span>
        </div>
        {editable ? (
          <button
            type="button"
            className="bn-bookmark-change"
            onClick={(e) => { e.stopPropagation(); setUrlDraft(url); setEditingUrl(true); }}
          >
            주소 변경
          </button>
        ) : null}
      </div>
    </div>
  );
}

export const bookmarkSpec = createReactBlockSpec(
  {
    type: 'bookmark',
    propSchema: {
      url: { default: '' },
      title: { default: '' },
      description: { default: '' },
    },
    content: 'none',
  },
  {
    render: ({ block, editor }) => <BookmarkView block={block} editor={editor} />,
  },
)();

export default bookmarkSpec;
